import { LocalContext } from "context/AuthenticationContext";
import { ContextType } from "react";
import {
  BehaviorSubject,
  from,
  Observable,
  skip,
  Subscription,
  switchMap,
} from "rxjs";

interface SkipInitialGuard {
  isLatest: () => boolean;
  runIfLatest: (callback: () => void) => void;
}

export abstract class BasedObservedViewModel<
  Input extends Disposable,
  Output extends Disposable,
  State extends Object = {},
> implements Disposable {
  input: Input;
  output: Output;
  context?: ContextType<typeof LocalContext>;
  subscriptions: Subscription = new Subscription();

  private _observedState: BehaviorSubject<State>;
  public readonly publicObservedState: Observable<State>;
  private isDisposed: boolean = false;

  constructor(input: Input, output: Output) {
    this.input = input;
    this.output = output;
    this._observedState = new BehaviorSubject<State>(
      this.createObservedState(),
    );
    this.publicObservedState = this._observedState.asObservable();
    this.transform();
  }

  protected abstract createObservedState(): State;

  public get observedState(): Readonly<State> {
    return this._observedState.value;
  }

  protected setObservedState(
    updater: Partial<State> | ((prev: State) => State),
  ): void {
    if (this.isDisposed) return;

    if (typeof updater === "function") {
      this._observedState.next(updater(this._observedState.value));
      return;
    }

    this._observedState.next({
      ...this._observedState.value,
      ...updater,
    });
  }

  protected skipFirstInitial<T>(
    observable: Observable<T>,
    next: (value: T, guard?: SkipInitialGuard) => void | Promise<void>,
  ): void {
    let version = 0;
    const sub = observable
      .pipe(
        skip(1),
        switchMap((value) => {
          const currentVersion = ++version;
          const guard: SkipInitialGuard = {
            isLatest: () => currentVersion === version,
            runIfLatest: (callback: () => void) => {
              if (currentVersion === version) {
                callback();
              }
            },
          };

          return from(Promise.resolve(next(value, guard)));
        }),
      )
      .subscribe();
    this.subscriptions.add(sub);
  }

  public resetObservedState(
    overrideState?: Partial<State> | ((initial: State) => State),
  ): void {
    if (this.isDisposed) {
      return;
    } else {
      const initialState = Object.freeze(this.observedState);

      if (!overrideState) {
        this._observedState.next(this.createObservedState());
        return;
      }

      if (typeof overrideState === "function") {
        this._observedState.next(overrideState(initialState));
        return;
      }

      this._observedState.next({
        ...initialState,
        ...overrideState,
      });
    }
  }

  protected transform(): void {}

  [Symbol.dispose](): void {
    this.isDisposed = true;
    this.input[Symbol.dispose]();
    this.output[Symbol.dispose]();
    this.subscriptions.unsubscribe();
    this._observedState.complete();
  }
}
